<script lang="ts" setup>
import { Zap } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, ChevronLeft, ChevronRight, FileText } from 'lucide-vue-next';
import {
    Dialog,
    DialogScrollContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { toast } from "@iframe-widgets/pages/widgets/toast/iframeToast";

const props = defineProps<{
    storeId: string;
}>();

const isDev = import.meta.env.DEV;

const listApiPath = storeOrdersApiClient[':storeId'].balance_invoices.$post;
type ListResponse = ResType<typeof listApiPath>["data"];
type InvoiceItem = ListResponse["items"][number];

const items = ref<InvoiceItem[]>([]);
const isLoading = ref(false);
const isGenerating = ref(false);
const hasNextPage = ref(false);
const currentCursor = ref<{ created_at_ts: number; id: string } | null>(null);
const cursors = ref<Array<{ created_at_ts: number; id: string } | null>>([null]);
const currentPageIndex = ref(0);

const viewingHtml = ref('');
const viewingTitle = ref('Invoice');
const isViewOpen = ref(false);
const invoiceFrameRef = ref<HTMLIFrameElement | null>(null);

/** Default = previous calendar month as YYYY-MM */
const previousMonthValue = () => {
    const d = new Date();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() - 1);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const devServiceMonth = ref(previousMonthValue());

const loadPage = async (cursor?: { created_at_ts: number; id: string } | null) => {
    isLoading.value = true;
    try {
        const { data, error } = await (await storeOrdersApiClient[':storeId'].balance_invoices.$post({
            param: { storeId: props.storeId },
            json: {
                cursor: cursor || undefined,
                pageSize: 10,
            },
        })).json();

        if (error) {
            toast.error('Failed to load invoices');
            console.error(error);
            return;
        }

        items.value = data?.items || [];
        hasNextPage.value = data?.hasNextPage || false;
        currentCursor.value = data?.nextCursor || null;
    } catch (e) {
        toast.error('Failed to load invoices');
        console.error(e);
    } finally {
        isLoading.value = false;
    }
};

const goToNextPage = () => {
    if (hasNextPage.value && currentCursor.value) {
        cursors.value.push(currentCursor.value);
        currentPageIndex.value++;
        loadPage(currentCursor.value);
    }
};

const goToPrevPage = () => {
    if (currentPageIndex.value > 0) {
        cursors.value.pop();
        currentPageIndex.value--;
        const prevCursor = cursors.value[currentPageIndex.value] ?? null;
        loadPage(prevCursor);
    }
};

const viewInvoice = (item: InvoiceItem) => {
    viewingHtml.value = item.html;
    viewingTitle.value = item.dataJson?.invoiceNumber || `${item.otp}-${item.serviceMonth}`;
    isViewOpen.value = true;
};

const printInvoice = () => {
    const frame = invoiceFrameRef.value;
    if (!frame?.contentWindow) {
        toast.error('Invoice not ready to print');
        return;
    }
    frame.contentWindow.focus();
    frame.contentWindow.print();
};

const resetAndReload = async () => {
    cursors.value = [null];
    currentPageIndex.value = 0;
    currentCursor.value = null;
    await loadPage(null);
};

const devGenerateMonthlyInvoice = async () => {
    if (!/^\d{4}-\d{2}$/.test(devServiceMonth.value)) {
        toast.error('Pick a valid month (YYYY-MM)');
        return;
    }
    isGenerating.value = true;
    try {
        const { data, error } = await (await storeOrdersApiClient[':storeId'].dev_generate_monthly_invoice.$post({
            param: { storeId: props.storeId },
            json: { serviceMonth: devServiceMonth.value },
        })).json();

        if (error) {
            toast.error('Dev generate failed');
            console.error(error);
            return;
        }

        toast.success(data?.created ? `Created ${data.invoiceId}` : `Already exists ${data?.invoiceId}`);
        await resetAndReload();
    } catch (e) {
        toast.error('Dev generate failed');
        console.error(e);
    } finally {
        isGenerating.value = false;
    }
};

const regeneratingInvoiceId = ref<string | null>(null);

const regenerateInvoice = async (item: InvoiceItem) => {
    regeneratingInvoiceId.value = item.invoiceId;
    try {
        const { data, error } = await (await storeOrdersApiClient[':storeId'].balance_invoice_regenerate.$post({
            param: { storeId: props.storeId },
            json: { serviceMonth: item.serviceMonth },
        })).json();

        if (error) {
            toast.error('Failed to regenerate invoice');
            console.error(error);
            return;
        }

        toast.success(`Regenerated ${data?.invoiceId}`);
        const pageCursor = cursors.value[currentPageIndex.value] ?? null;
        await loadPage(pageCursor);
    } catch (e) {
        toast.error('Failed to regenerate invoice');
        console.error(e);
    } finally {
        regeneratingInvoiceId.value = null;
    }
};

onMounted(() => {
    loadPage(null);
});
</script>

<template>
    <div class="space-y-4">
        <details v-if="isDev">
            <summary>
                <Zap class="h-5 w-5 text-yellow-500 inline-block cursor-pointer" />
                Debug
            </summary>
            <div class="space-y-6 p-4 border rounded-lg">

                <Input v-model="devServiceMonth"
                       type="month"
                       class="w-[10.5rem] h-8" />
                <Button variant="destructive"
                        size="sm"
                        :disabled="isGenerating || isLoading"
                        @click="devGenerateMonthlyInvoice">
                    <Loader2 v-if="isGenerating"
                             class="animate-spin mr-1 h-4 w-4" />
                    DEV: Generate month
                </Button>
            </div>
        </details>

        <div v-if="isLoading && items.length === 0"
             class="flex justify-center items-center py-8">
            <Loader2 class="h-6 w-6 animate-spin" />
            <span class="ml-2">Loading invoices...</span>
        </div>

        <div v-else-if="items.length === 0"
             class="text-center py-8">
            <FileText class="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p class="text-muted-foreground">No invoices yet</p>
        </div>

        <div v-else
             class="space-y-3">
            <div v-for="item in items"
                 :key="item.invoiceId"
                 class="p-3 border rounded-lg flex flex-wrap items-center justify-between gap-3">
                <div class="space-y-1">
                    <div class="font-medium">
                        {{ item.dataJson?.invoiceNumber || `${item.otp}-${item.serviceMonth}` }}
                    </div>
                    <div class="flex flex-wrap gap-2">
                        <Badge variant="outline"
                               class="text-xs">
                            {{ item.dataJson?.serviceMonthLabel || item.serviceMonth }}
                        </Badge>
                        <Badge variant="outline"
                               class="text-xs"
                               v-if="item.dataJson?.invoiceDateLabel">
                            {{ item.dataJson.invoiceDateLabel }}
                        </Badge>
                    </div>
                </div>
                <div class="flex gap-2">
                    <Button v-if="isDev"
                            variant="outline"
                            size="sm"
                            :disabled="regeneratingInvoiceId === item.invoiceId || isLoading"
                            @click="regenerateInvoice(item)">
                        <Loader2 v-if="regeneratingInvoiceId === item.invoiceId"
                                 class="animate-spin mr-1 h-4 w-4" />
                        <Zap class="h-5 w-5 text-yellow-500 inline-block cursor-pointer" />
                        DEV: Regenerate
                    </Button>
                    <Button variant="outline"
                            size="sm"
                            @click="viewInvoice(item)">
                        View
                    </Button>
                </div>
            </div>
        </div>

        <div class="flex items-center justify-between pt-2"
             v-if="items.length > 0 || currentPageIndex > 0">
            <Button variant="outline"
                    size="sm"
                    :disabled="currentPageIndex === 0 || isLoading"
                    @click="goToPrevPage">
                <ChevronLeft class="h-4 w-4 mr-1" />
                Prev
            </Button>
            <span class="text-sm text-muted-foreground">Page {{ currentPageIndex + 1 }}</span>
            <Button variant="outline"
                    size="sm"
                    :disabled="!hasNextPage || isLoading"
                    @click="goToNextPage">
                Next
                <ChevronRight class="h-4 w-4 ml-1" />
            </Button>
        </div>

        <Dialog :open="isViewOpen"
                @update:open="isViewOpen = $event"
                class="min-h-full">
            <DialogScrollContent class="my-0 flex h-[95vh] max-h-[95vh] w-full max-w-3xl flex-col gap-2 min-h-full">
                <DialogHeader class="shrink-0">
                    <DialogTitle>{{ viewingTitle }}</DialogTitle>
                </DialogHeader>
                <div class="flex shrink-0 justify-end">
                    <Button variant="outline"
                            size="sm"
                            @click="printInvoice">
                        Print / Save as PDF
                    </Button>
                </div>
                <iframe ref="invoiceFrameRef"
                        v-if="viewingHtml"
                        title="Invoice"
                        class="min-h-0 w-full flex-1 border-0 bg-white"
                        :srcdoc="viewingHtml" />
            </DialogScrollContent>
        </Dialog>
    </div>
</template>
